import { useState } from 'react';
import type { Transaction, Labels, TransactionType, PendingStatus } from '../../types/accountReceivable';
import { ReceiptModal } from './ReceiptModal';

interface TransactionRowProps {
  transaction: Transaction;
  labels: Labels;
}

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatCurrency(value: number): string {
  if (value < 0) {
    return `$ -${currencyFormatter.format(Math.abs(value)).replace(/^\$\s*/, '')}`;
  }
  return currencyFormatter.format(value);
}

const TYPE_COLORS: Record<TransactionType, string> = {
  order: 'bg-blue-500',
  invoice: 'bg-cyan-500',
  receipt: 'bg-green-500',
  summary: 'bg-gray-500',
  credit_note: 'bg-amber-500',
  other: 'bg-gray-400',
};

function PendingStatusBadge({ status }: { status: PendingStatus }) {
  if (!status) return null;

  if (status.type === 'paid') {
    return (
      <span className="ml-1.5 inline-block rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
        saldado
      </span>
    );
  }

  return (
    <span className="ml-1.5 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
      pendiente {formatCurrency(status.amount)}
    </span>
  );
}

function TransferStatusBadge({ status }: { status: 'verified' | 'pending' | null }) {
  if (!status) return null;

  if (status === 'verified') {
    return (
      <span className="ml-1.5 inline-block rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
        verificada
      </span>
    );
  }

  return (
    <span className="ml-1.5 inline-block rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700">
      sin verificar
    </span>
  );
}

function ActionButton({ transaction }: { transaction: Transaction }) {
  const [showReceipt, setShowReceipt] = useState(false);

  if (transaction.type === 'order' && transaction.link) {
    return (
      <a href={transaction.link} className="rounded bg-gray-100 px-2.5 py-1 text-[12px] font-medium text-gray-600 no-underline transition-colors hover:bg-gray-200 hover:text-gray-800">
        Ver pedido
      </a>
    );
  }

  if (transaction.type === 'invoice' || transaction.type === 'credit_note') {
    return (
      <a
        href={`/cuenta-corriente/factura/${transaction.source_id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="rounded bg-gray-100 px-2.5 py-1 text-[12px] font-medium text-gray-600 no-underline transition-colors hover:bg-gray-200 hover:text-gray-800"
      >
        Ver PDF
      </a>
    );
  }

  if (transaction.type === 'receipt' && transaction.receipt_id) {
    return (
      <>
        <button
          onClick={() => setShowReceipt(true)}
          className="rounded bg-gray-100 px-2.5 py-1 text-[12px] font-medium text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-800"
        >
          Ver
        </button>
        {showReceipt && (
          <ReceiptModal
            receiptId={transaction.receipt_id}
            onClose={() => setShowReceipt(false)}
          />
        )}
      </>
    );
  }

  return null;
}

export function TransactionRow({ transaction, labels }: TransactionRowProps) {
  const isPositive = transaction.amount > 0;
  const badgeClass = TYPE_COLORS[transaction.type] || TYPE_COLORS.other;

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      {/* Mobile */}
      <div className="block p-3 sm:p-4 md:hidden">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[13px] text-gray-500">{transaction.date_formatted}</span>
          <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium uppercase text-white ${badgeClass}`}>
            {labels.types[transaction.type] || labels.types.other}
          </span>
        </div>
        <div className="mb-2 text-sm text-gray-800">
          {transaction.description}
          <PendingStatusBadge status={transaction.pending_status} />
          <TransferStatusBadge status={transaction.transfer_status} />
        </div>
        <div className="flex items-end justify-between">
          <div className="flex flex-col gap-0.5">
            <span className={`text-sm font-semibold ${isPositive ? 'text-red-500' : 'text-green-500'}`}>
              {formatCurrency(transaction.amount)}
            </span>
            <span className="text-xs text-gray-500">
              {labels.runningBalance}: {formatCurrency(transaction.balance)}
            </span>
          </div>
          <ActionButton transaction={transaction} />
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden items-center gap-3 px-4 py-3 md:grid" style={{ gridTemplateColumns: '100px 100px 1fr 120px 120px 80px' }}>
        <div className="text-[13px] text-gray-500">{transaction.date_formatted}</div>
        <div>
          <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium uppercase text-white ${badgeClass}`}>
            {labels.types[transaction.type] || labels.types.other}
          </span>
        </div>
        <div className="min-w-0 text-sm text-gray-800">
          <span className="truncate">{transaction.description}</span>
          <PendingStatusBadge status={transaction.pending_status} />
          <TransferStatusBadge status={transaction.transfer_status} />
        </div>
        <div className={`text-right text-sm font-medium ${isPositive ? 'text-red-500' : 'text-green-500'}`}>
          {formatCurrency(transaction.amount)}
        </div>
        <div className="text-right text-sm font-medium text-gray-800">{formatCurrency(transaction.balance)}</div>
        <div className="text-center">
          <ActionButton transaction={transaction} />
        </div>
      </div>
    </div>
  );
}
